import React, { useState } from 'react';
import { Tab, Tabs, Typography, Box } from '@mui/material';
import EqualisationsConcertina from 'components/EqualisationElements/EqualisationsConcertina';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import InteractiveTable from 'components/InteractiveTable';
import Heading from 'components/Heading';
import { sortAllocations } from '../utils/sortAllocations';

interface FeeAllocation {
  commitmentId: string;
  investorName: string;
  feeExTaxLcy: number;
  salesTaxAmountLcy: number;
  feeIncTaxLcy: number;
  isTransferor: boolean;
  isNewTransferee?: boolean;
}

interface FeeCardProps {
  fee: {
    feeKey: string;
    originalFeeId: string;
    lpFeeTemplateFeeName: string;
    accrualDate: CalendarDay;
    approvalDate?: CalendarDay;
    isApproved: boolean;
    originalAllocations: Array<FeeAllocation>;
    newAllocations: Array<FeeAllocation>;
  };
  index: number;
  isLastElement: boolean;
  baseCurrency: string;
  transferDate: CalendarDay;
}

const FeeCard: React.FC<FeeCardProps> = ({
  fee,
  index,
  isLastElement,
  baseCurrency,
  transferDate,
}) => {
  const [activeTab, setActiveTab] = useState(1);

  const handleTabChange = (_: unknown, newValue: number) => {
    setActiveTab(newValue);
  };

  const sortedOriginalAllocations = sortAllocations(fee.originalAllocations);
  const sortedNewAllocations = sortAllocations(fee.newAllocations);

  const originalRows = sortedOriginalAllocations.map((allocation, idx) => ({
    id: `original-${idx}`,
    investorName: allocation.investorName,
    feeExTaxLcy: allocation.feeExTaxLcy,
    salesTaxAmountLcy: allocation.salesTaxAmountLcy,
    feeIncTaxLcy: allocation.feeIncTaxLcy,
  }));

  const newRows = sortedNewAllocations.map((allocation, idx) => ({
    id: `new-${idx}`,
    investorName: allocation.investorName,
    feeExTaxLcy: allocation.feeExTaxLcy,
    salesTaxAmountLcy: allocation.salesTaxAmountLcy,
    feeIncTaxLcy: allocation.feeIncTaxLcy,
  }));

  const rows = activeTab === 0 ? originalRows : newRows;

  const columns = [
    {
      field: 'investorName',
      headerName: 'Limited partner',
      renderCell: ({ row }) => row.investorName,
      flex: 1,
    },
    {
      field: 'feeExTaxLcy',
      headerName: 'Fee',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.feeExTaxLcy, {
          fallback: '-',
          currency: baseCurrency,
        }),
      flex: 1,
    },
    {
      field: 'salesTaxAmountLcy',
      headerName: 'Sales tax',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.salesTaxAmountLcy, {
          fallback: '-',
          currency: baseCurrency,
        }),
      flex: 1,
    },
    {
      field: 'feeIncTaxLcy',
      headerName: 'Total charge',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.feeIncTaxLcy, {
          fallback: '-',
          currency: baseCurrency,
        }),
      flex: 1,
    },
  ];

  return (
    <EqualisationsConcertina
      key={index}
      gridTemplateColumns="60% 20% 18% 2%"
      isLastElement={isLastElement}
      expandedContent={
        <Box sx={{ marginTop: '-12px', paddingTop: 0 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            sx={{ marginBottom: 2, marginTop: 0 }}
          >
            <Tab
              label={
                <Heading variant="h5" noMargin>
                  Allocation before {transferDate?.toLocalDateLabel()}
                </Heading>
              }
            />
            <Tab
              label={
                <Heading variant="h5" noMargin>
                  Allocation after {transferDate?.toLocalDateLabel()}
                </Heading>
              }
            />
          </Tabs>
          <InteractiveTable
            id={`fee-breakdown-table-${fee.feeKey}`}
            columns={columns}
            rows={rows}
            scrollable={true}
          />
        </Box>
      }
      concertinaItems={[
        <Typography variant="body1" key="name">
          {fee.lpFeeTemplateFeeName ?? ''}
        </Typography>,
        <Typography variant="body1" key="status">
          {fee.isApproved ? 'Approved' : 'Draft'}
        </Typography>,
        <Typography variant="body1" key="accrual-date">
          {fee.accrualDate ? fee.accrualDate.toLocalDateLabel() : '-'}
        </Typography>,
      ]}
    />
  );
};

export default FeeCard;
